// ═════════════════════════════════════════════════════════════════════
//  cataloguePdf.js — true-vector branded PDF export for the digital
//  catalogue, mirroring resources/designs/catalogue_template.html.
//
//  The design is reproduced with pdfmake vector primitives + brand
//  fonts (Fugaz One for the logo, Outfit for body copy) + colors:
//    • paper page background (#f3f3f1) with the gold blob, drawn as a
//      canvas `path` on each page's bottom-left
//    • header: outlined logo text (layered vector text approximates the
//      CSS text-stroke + blue/gold offset shadows) + gold brand line
//    • 3-column grid of white "cards" (photo, name, Harga, Min. Order)
//    • right-aligned legal footer line like the template
//
//  Product photos are the only raster content (inherent to photos);
//  everything else is vector. No cover page, no watermark.
// ═════════════════════════════════════════════════════════════════════
import { formatIDR, formatLongDate, formatNumber, minOrderLabel, toISODate } from './format';
import { fileFetchUrl } from '../services/api';
import { create as createFont } from 'fontkit';

// Font files bundled as data URIs (OFL licensed). Registered with
// pdfmake at export time so the PDF uses the template's typefaces.
import fugazOneUrl from '../assets/fonts/FugazOne-Regular.ttf?inline';
import outfitRegularUrl from '../assets/fonts/Outfit-Regular.ttf?inline';
import outfitMediumUrl from '../assets/fonts/Outfit-Medium.ttf?inline';
import outfitSemiBoldUrl from '../assets/fonts/Outfit-SemiBold.ttf?inline';
import outfitBoldUrl from '../assets/fonts/Outfit-Bold.ttf?inline';
import outfitExtraBoldUrl from '../assets/fonts/Outfit-ExtraBold.ttf?inline';

const C = {
  ink: '#2b2b33',
  muted: '#8a8a92',
  paper: '#f3f3f1',
  card: '#ffffff',
  blue: '#1f6fe0',
  blueDark: '#1149a8',
  gold: '#d1a72e',
  goldDeep: '#c99a1e',
  border: '#d8d8d3',
};

const BORDER_W = 0.6;

// Card name area width: A4 content width (595.28 - 72) split into 3 columns,
// minus the card's horizontal margins (12 + 12). Used to predict how many
// lines a product name will wrap to.
const NAME_W = (595.28 - 72) / 3 - 24;
const NAME_LINES = 2;

const F = {
  logo: 'FugazOne',
  body: 'Outfit',
  medium: 'OutfitMedium',
  semiBold: 'OutfitSemiBold',
  bold: 'OutfitBold',
  extraBold: 'OutfitExtraBold',
};

const STORE_NAME = 'Seroja Makmur';
const LEGAL_NAME = 'CV. Pancuran Emas Abadi';
const NEGO_NOTE = 'Harga bisa dinego tergantung pada jumlah barang yang dipesan';

// Keep the downloaded filename plain and safe (only letters, digits, dash,
// underscore) so Android's MIME sniffing always sees a clean `katalog-*.pdf`.
const safeCode = (value) =>
  String(value ?? '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'katalog';

// Blob shape from the template's SVG (viewBox 0 0 300 300).
const BLOB_PATH = [
  'M 179.44 37.00 L 179.44 37.00 Q 150.00 20.00 120.56 37.00',
  'L 66.86 68.00 Q 37.42 85.00 37.42 119.00',
  'L 37.42 181.00 Q 37.42 215.00 66.86 232.00',
  'L 120.56 263.00 Q 150.00 280.00 179.44 263.00',
  'L 233.14 232.00 Q 262.58 215.00 262.58 181.00',
  'L 262.58 119.00 Q 262.58 85.00 233.14 68.00 Z',
].join(' ');

// Scale + translate a canvas path (pdfmake paths can't be offset or
// transformed as a whole, so the numbers are rewritten in place).
function transformPath(path, dx, dy, k) {
  let i = 0;
  return path.replace(/(-?\d*\.?\d+)/g, (m) => {
    const v = parseFloat(m) * k;
    const out = i % 2 === 0 ? v + dx : v + dy;
    i += 1;
    return String(Math.round(out * 100) / 100);
  });
}

function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

// ——— fonts ───────────────────────────────────────────────────────────
const base64 = (dataUri) => String(dataUri).split(',')[1] ?? String(dataUri);

export function registerCatalogueFonts(pdfMake) {
  const vfs = {
    'FugazOne-Regular.ttf': { data: base64(fugazOneUrl), encoding: 'base64' },
    'Outfit-Regular.ttf': { data: base64(outfitRegularUrl), encoding: 'base64' },
    'Outfit-Medium.ttf': { data: base64(outfitMediumUrl), encoding: 'base64' },
    'Outfit-SemiBold.ttf': { data: base64(outfitSemiBoldUrl), encoding: 'base64' },
    'Outfit-Bold.ttf': { data: base64(outfitBoldUrl), encoding: 'base64' },
    'Outfit-ExtraBold.ttf': { data: base64(outfitExtraBoldUrl), encoding: 'base64' },
  };
  pdfMake.addFontContainer({
    vfs,
    fonts: {
      [F.logo]: { normal: 'FugazOne-Regular.ttf' },
      [F.body]: { normal: 'Outfit-Regular.ttf' },
      [F.medium]: { normal: 'Outfit-Medium.ttf' },
      [F.semiBold]: { normal: 'Outfit-SemiBold.ttf' },
      [F.bold]: { normal: 'Outfit-Bold.ttf' },
      [F.extraBold]: { normal: 'Outfit-ExtraBold.ttf' },
    },
  });
}

// ——— page furniture ──────────────────────────────────────────────────
function pageBackground(pageSize) {
  const blobSize = 200;
  const blob = {
    type: 'path',
    d: transformPath(BLOB_PATH, -55, pageSize.height - 140, blobSize / 300),
    color: C.gold,
  };
  return {
    canvas: [
      { type: 'rect', x: 0, y: 0, w: pageSize.width, h: pageSize.height, color: C.paper },
      blob,
    ],
  };
}

// The template logo is white text with a blue-dark stroke and blue /
// blue-dark offset shadows. pdfmake can't stroke text, so we layer
// Fugaz One text nodes: 8 outline copies, then the two shadow copies,
// then the white core on top — all overlaid with absolutePosition.
// absolutePosition is page-absolute (ignores pageMargins), so every
// layer is offset by the content-area origin (36, 38) to sit inside
// the margins.
const PAGE_MARGIN_X = 36;
const PAGE_MARGIN_Y = 38;

function outlinedLogoText(text, size) {
  const stroke = Math.round(size * 0.06 * 100) / 100;
  const layer = (color, dx, dy) => ({
    text,
    font: F.logo,
    fontSize: size,
    lineHeight: 1,
    color,
    absolutePosition: { x: PAGE_MARGIN_X + dx, y: PAGE_MARGIN_Y + dy },
  });
  const offsets = [
    [stroke, 0], [-stroke, 0], [0, stroke], [0, -stroke],
    [stroke, stroke], [stroke, -stroke], [-stroke, stroke], [-stroke, -stroke],
  ];
  const shadowBlue = Math.round(size * 0.09 * 100) / 100;
  const shadowDark = Math.round(size * 0.14 * 100) / 100;

  return {
    stack: [
      { canvas: [{ type: 'rect', x: 0, y: 0, w: 1, h: size * 1.05, color: '#ffffff', opacity: 0 }] },
      ...offsets.map(([dx, dy]) => layer(C.blueDark, dx, dy)),
      layer(C.blue, shadowBlue, shadowBlue),
      layer(C.blueDark, shadowDark, shadowDark),
      layer('#ffffff', 0, 0),
    ],
  };
}

function emptyState() {
  return {
    text: 'Tidak ada item pada katalog ini.',
    color: C.muted,
    fontSize: 9,
    alignment: 'center',
    margin: [0, 12, 0, 12],
  };
}

// ——— item card ───────────────────────────────────────────────────────
const PHOTO_W = 141;
const PHOTO_H = 130; // aspect 1 : 0.92 like the template thumb

// pdfmake can't clamp text to a fixed number of lines, so we measure the
// product name with fontkit (the same font engine pdfmake uses) and pad
// short names with a spacer line / truncate long names so every card's
// name area is exactly NAME_LINES tall.
const fontCache = new Map();

function getFont(url) {
  if (!fontCache.has(url)) {
    const bytes = Uint8Array.from(atob(base64(url)), (c) => c.charCodeAt(0));
    fontCache.set(url, createFont(bytes));
  }
  return fontCache.get(url);
}

function textWidth(text, fontSize, fontUrl = outfitSemiBoldUrl) {
  const font = getFont(fontUrl);
  const layout = font.layout(text);
  const units = layout.positions.reduce((acc, p) => acc + p.xAdvance, 0);
  return (units * fontSize) / font.unitsPerEm;
}

function greedyLines(text, maxWidth, fontSize) {
  let line = '';
  let count = 1;
  for (const word of text.split(' ')) {
    const candidate = line ? `${line} ${word}` : word;
    if (textWidth(candidate, fontSize) > maxWidth) {
      count += 1;
      line = word;
    } else {
      line = candidate;
    }
  }
  return count;
}

function nameText(name) {
  const fontSize = 11;
  const fit = (t) => greedyLines(t, NAME_W, fontSize);
  const pad = (text) => [text, '\n', { text: '.', color: C.card }]; // white glyph keeps the spacer line
  let body = name;
  if (fit(body) === 1) {
    return {
      text: pad(body),
      font: F.semiBold,
      fontSize,
      lineHeight: 1.25,
      color: C.ink,
      margin: [0, 7, 0, 4],
    };
  }
  if (fit(body) > NAME_LINES) {
    while (body.length > 0 && fit(body) > NAME_LINES) body = body.slice(0, -1);
    body = `${body.slice(0, -1)}…`;
    if (fit(body) === 1) {
      return {
        text: pad(body),
        font: F.semiBold,
        fontSize,
        lineHeight: 1.25,
        color: C.ink,
        margin: [0, 7, 0, 4],
      };
    }
  }
  return {
    text: body,
    font: F.semiBold,
    fontSize,
    lineHeight: 1.25,
    color: C.ink,
    margin: [0, 7, 0, 4],
  };
}

function photoBlock(dataUrl) {
  if (dataUrl) {
    return {
      image: dataUrl,
      cover: { width: PHOTO_W, height: PHOTO_H, align: 'center', valign: 'center' },
    };
  }
  // The "Tanpa foto" label overlays the gray box as a detached block so the
  // placeholder measures exactly PHOTO_H tall (same as a real photo).
  const label = {
    text: 'Tanpa foto',
    fontSize: 7.5,
    color: C.muted,
    relativePosition: {
      x: (NAME_W - textWidth('Tanpa foto', 7.5, outfitRegularUrl)) / 2,
      y: -58,
    },
  };
  return {
    stack: [
      {
        canvas: [
          {
            type: 'rect',
            x: 0,
            y: 0,
            w: PHOTO_W,
            h: PHOTO_H,
            color: '#e9e9e6',
            lineColor: '#d8d8d3',
            lineWidth: 0.6,
          },
        ],
        alignment: 'center',
      },
      label,
    ],
  };
}

function compactPrice(value) {
  return `Rp${formatIDR(value).replace(/^Rp\s*/, '')}`;
}

function itemCard(item) {
  const minOrder = minOrderLabel(item.pack_qty, item.unit);
  return {
    stack: [
      photoBlock(item._photo),
      nameText(item.item_name),
      minOrder && {
        text: `Min. Order ${minOrder}`,
        fontSize: 7.5,
        color: C.muted,
        margin: [0, 2, 0, 4],
      },
      {
        text: [
          { text: compactPrice(item.item_price), font: F.bold, fontSize: 12, color: C.ink },
          { text: ' /pc', font: F.body, fontSize: 8.5, color: C.muted },
        ],
      },
    ].filter(Boolean),
    margin: [12, 9, 12, 10],
  };
}

function gridTable(items) {
  if (items.length === 0) return { stack: [emptyState()] };

  const body = chunk(items, 3).map((row) =>
    row.map((item) => ({ stack: [itemCard(item)], fillColor: C.card })),
  );
  const lastRow = body[body.length - 1];
  while (lastRow.length < 3) lastRow.push({ text: '', fillColor: C.card });

  return {
    table: {
      headerRows: 0,
      widths: ['*', '*', '*'],
      body,
    },
    layout: {
      hLineWidth: () => BORDER_W,
      vLineWidth: () => BORDER_W,
      hLineColor: () => C.border,
      vLineColor: () => C.border,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
  };
}

// ——— document ────────────────────────────────────────────────────────
/**
 * Builds the pdfmake docDefinition for a catalogue. Pure — takes the
 * enriched items (each optionally carrying `_photo` as a data URL) and
 * the catalogue descriptor, so it can be snapshot-checked offline.
 * @param {Array} items enriched items from getCatalogueItems
 * @param {object} catalogue enriched catalogue (name, code, edition, remarks)
 */
export function buildCatalogueDocDefinition(items, catalogue) {
  const logoSize = catalogue.catalogue_name && catalogue.catalogue_name.length > 18 ? 24 : 30;

  const brandBlock = {
    stack: [
      {
        text: STORE_NAME.toUpperCase(),
        font: F.extraBold,
        fontSize: 12,
        color: C.goldDeep,
        characterSpacing: 3,
        alignment: 'right',
      },
      {
        text: `${catalogue.catalogue_code} · Edisi ${formatLongDate(catalogue.edition)} · ${formatNumber(items.length)} item`,
        fontSize: 8,
        color: C.muted,
        alignment: 'right',
        margin: [0, 7, 0, 0],
      },
    ],
    width: '*',
    alignment: 'right',
  };
  if (catalogue.remarks) {
    brandBlock.stack.push({
      text: catalogue.remarks,
      fontSize: 8,
      color: C.muted,
      alignment: 'right',
      margin: [0, 3, 0, 0],
    });
  }

  const header = {
    columns: [outlinedLogoText(catalogue.catalogue_name || 'Katalog', logoSize), brandBlock],
    columnGap: 18,
    margin: [0, 0, 0, 30],
  };

  return {
    pageSize: 'A4',
    pageMargins: [24, 24, 24, 32],
    defaultStyle: { font: F.body, fontSize: 9, color: C.ink },
    info: {
      title: `Katalog ${catalogue.catalogue_name}`,
      author: `${STORE_NAME} (${LEGAL_NAME})`,
      subject: `Katalog produk ${catalogue.catalogue_name} — ${formatNumber(items.length)} item`,
      keywords: ['katalog', 'catalogue', catalogue.catalogue_name, STORE_NAME].join(', '),
      creator: `${STORE_NAME} — Katalog Digital`,
      producer: 'pdfmake',
    },
    background: (currentPage, pageSize) => pageBackground(pageSize),
    footer: () => ({
      margin: [36, 0, 36, 0],
      stack: [
        {
          columns: [
            // {
            //   text: `${currentPage} / ${pageCount}`,
            //   fontSize: 8,
            //   color: C.muted,
            // },
            {
              text: NEGO_NOTE,
              fontSize: 12,
              color: C.ink,
              alignment: 'center',
              margin: [0, 4, 0, 0],
            },
          ],
        },
      ],
    }),
    content: [header, gridTable(items)],
  };
}

async function toDataUrl(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ——— PDF size budget ─────────────────────────────────────────────────
// The embedded brand fonts (~266KB of TTF bytes) plus the vector page
// furniture set a fixed floor; product photos are the only variable cost.
// Card photos render at ~141pt wide, so anything beyond ~720px is already
// invisible — we downscale each photo and only step the compression up
// while the size estimate is still over the 5MB budget.
const PDF_SIZE_BUDGET = 5 * 1024 * 1024;
const PDF_SIZE_MARGIN = 0.95;
const PDF_FONT_OVERHEAD = 320 * 1024; // six embedded fonts + font defs
const PDF_LAYOUT_OVERHEAD = 96 * 1024; // vectors, header, metadata, footer
const PDF_PER_IMAGE_OVERHEAD = 4 * 1024; // image xobject + flate stream meta
const CARDS_PER_PAGE = 18; // A4, 3-col grid, ~6 rows

const COMPRESSION_STAGES = [
  { maxDim: 720, quality: 0.85 },
  { maxDim: 520, quality: 0.78 },
  { maxDim: 360, quality: 0.7 },
  { maxDim: 256, quality: 0.65 },
];

const approxBytes = (dataUrl) =>
  dataUrl ? Math.ceil(String(dataUrl).length * 0.75) : 0;

function estimatePdfBytes(photos) {
  const imageBytes = photos.reduce((sum, p) => sum + approxBytes(p), 0);
  const imageCount = photos.filter(Boolean).length;
  return (
    PDF_FONT_OVERHEAD +
    PDF_LAYOUT_OVERHEAD +
    imageBytes +
    imageCount * PDF_PER_IMAGE_OVERHEAD +
    Math.ceil(photos.length / CARDS_PER_PAGE) * 8 * 1024
  );
}

/**
 * Downscales a data-URL image to `maxDim` and re-encodes it as a JPEG.
 * Passes the original through untouched when it is already small enough
 * (avoiding a wasteful extra JPEG generation) or not decodable.
 */
function recompressJpeg(dataUrl, { maxDim, quality }) {
  if (!dataUrl) return null;
  if (!/^data:image\//i.test(String(dataUrl))) return dataUrl;
  return new Promise((resolve) => {
    const image = new Image();
    image.onerror = () => resolve(dataUrl);
    image.onload = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      if (width <= 0 || height <= 0) return resolve(dataUrl);
      const scale = Math.min(1, maxDim / Math.max(width, height));
      if (scale === 1) return resolve(dataUrl);
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(dataUrl);
      ctx.drawImage(image, 0, 0, w, h);
      try {
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch {
        resolve(dataUrl);
      }
    };
    image.src = String(dataUrl);
  });
}

/**
 * Fetches each item photo once, then re-encodes it across progressively
 * stronger compression stages until the estimated finished PDF fits the
 * 5MB budget. Items get an enriched `_photo` data URL (or null).
 * @param {Array} items items with a `picture` field
 */
export async function attachCompressedPhotos(items) {
  const originals = await Promise.all(
    items.map(async (item) => {
      const url = item.picture ? fileFetchUrl(item.picture) : null;
      return url ? toDataUrl(url) : null;
    }),
  );

  let photos = originals.slice();
  for (const stage of COMPRESSION_STAGES) {
    photos = await Promise.all(
      originals.map((dataUrl) => recompressJpeg(dataUrl, stage)),
    );
    if (estimatePdfBytes(photos) <= PDF_SIZE_BUDGET * PDF_SIZE_MARGIN) break;
  }

  return items.map((item, i) => ({ ...item, _photo: photos[i] }));
}

/**
 * `Katalog_Seroja_{edition}_{name}.pdf`, the name used for the download and the WhatsApp upload.
 * @param {object} catalogue
 */
export function cataloguePdfFilename(catalogue) {
  const editionDate = catalogue.edition
    ? String(catalogue.edition).slice(0, 10)
    : toISODate(new Date());
  return `Katalog_Seroja_${editionDate}_${safeCode(catalogue.catalogue_name)}.pdf`;
}

async function createCataloguePdf({ items, catalogue }) {
  const enriched = await attachCompressedPhotos(items);

  const [{ default: pdfMake }, { default: fontContainer }] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/fonts/Roboto'),
  ]);
  pdfMake.addFontContainer(fontContainer);
  registerCatalogueFonts(pdfMake);
  return pdfMake.createPdf(buildCatalogueDocDefinition(enriched, catalogue));
}

/**
 * Same PDF as the download, returned as a Blob (e.g. to upload it for WhatsApp).
 * @param {{ items: Array, catalogue: object }} opts
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export async function generateCataloguePdfBlob({ items, catalogue }) {
  const pdf = await createCataloguePdf({ items, catalogue });
  return { blob: await pdf.getBlob(), filename: cataloguePdfFilename(catalogue) };
}

/**
 * Fetches each item photo, embeds it in the grid, and downloads the PDF.
 * @param {{ items: Array, catalogue: object }} opts
 */
export default async function generateCataloguePdf({ items, catalogue }) {
  const pdf = await createCataloguePdf({ items, catalogue });
  await pdf.download(cataloguePdfFilename(catalogue));
}

// Shared primitives reused by the promotion PDF (promotionPdf.js) so the
// two exports stay visually consistent: brand constants, page background,
// outlined logo text, the item card grid, filename sanitizer and the
// data-URL image loader.
export {
  C,
  F,
  NEGO_NOTE,
  STORE_NAME,
  LEGAL_NAME,
  safeCode,
  pageBackground,
  outlinedLogoText,
  gridTable,
  toDataUrl,
};
